import React, { useEffect, useState } from "react";
import { getFirestore, doc, collection, query, where, limit, onSnapshot, getDoc } from "firebase/firestore";
import app from "../../firebase";
import { getActiveSemester, useCalendarClock } from "../../utils/announcementDates";

export default function StudentFinance({ studentId }) {
	const todayKey = useCalendarClock();
	const [units, setUnits] = useState(0);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [student, setStudent] = useState(null);
	const [courses, setCourses] = useState([]);
	const [fees, setFees] = useState([]);
	const [cashDiscount, setCashDiscount] = useState(0);
	const [cashWithDiscountDate, setCashWithDiscountDate] = useState("");
	const [cashWithoutDiscountDate, setCashWithoutDiscountDate] = useState("");
	const [installmentDates, setInstallmentDates] = useState([]);
	const [feeSettings, setFeeSettings] = useState(null);
	const [announcementSettings, setAnnouncementSettings] = useState(null);

	useEffect(() => {
		if (!studentId) return;
		setLoading(true);
		const db = getFirestore(app);

		// Fetch student data by idNumber (studentId)
		const q = query(collection(db, 'students'), where('idNumber', '==', studentId), limit(1));
		const unsub = onSnapshot(q, async (qsnap) => {
			if (!qsnap.empty) {
				const docSnap = qsnap.docs[0];
				const data = docSnap.data();
				setStudent({ id: docSnap.id, ...data });
				// Compute total units from totalUnits field or schedules array
				const totalUnits = typeof data.totalUnits === 'number' ? data.totalUnits : (Array.isArray(data.schedules) ? data.schedules.reduce((s, c) => s + (parseFloat(c.units) || 0), 0) : 0);
				setUnits(totalUnits);

				// Fetch fees from Firestore
				const feesRef = doc(db, "systemSettings", "miscellaneousFees");
				const feesSnap = await getDoc(feesRef);
				if (feesSnap.exists()) {
					const feesData = feesSnap.data();
					setFeeSettings(feesData);

					// Determine current semester based on announcement semester dates
					const announcementRef = doc(db, "systemSettings", "announcement");
					const announcementSnap = await getDoc(announcementRef);
					setAnnouncementSettings(announcementSnap.exists() ? announcementSnap.data() : null);
				}
			} else {
				setError("Student not found.");
			}
			setLoading(false);
		}, (err) => {
			setError("Failed to fetch student data.");
			setLoading(false);
		});
		return () => unsub();
	}, [studentId]);

	useEffect(() => {
		if (!feeSettings) return;
		setFees(feeSettings.fees || []);
		if (getActiveSemester(announcementSettings || {}, todayKey) === "second") {
			setCashDiscount(feeSettings.secondSemCashDiscount ?? feeSettings.cashDiscount ?? 0);
			setCashWithDiscountDate(feeSettings.secondSemCashWithDiscountDate || "");
			setCashWithoutDiscountDate(feeSettings.secondSemCashWithoutDiscountDate || "");
			setInstallmentDates(feeSettings.secondSemInstallmentDates || []);
		} else if (getActiveSemester(announcementSettings || {}, todayKey) === "first") {
			setCashDiscount(feeSettings.cashDiscount ?? 0);
			setCashWithDiscountDate(feeSettings.firstSemCashWithDiscountDate || feeSettings.cashWithDiscountDate || "");
			setCashWithoutDiscountDate(feeSettings.firstSemCashWithoutDiscountDate || feeSettings.cashWithoutDiscountDate || "");
			setInstallmentDates(feeSettings.firstSemInstallmentDates || feeSettings.installmentDates || []);
		} else {
			setCashDiscount(feeSettings.cashDiscount ?? 0);
			setCashWithDiscountDate(feeSettings.cashWithDiscountDate || "");
			setCashWithoutDiscountDate(feeSettings.cashWithoutDiscountDate || "");
			setInstallmentDates(feeSettings.installmentDates || []);
		}
	}, [feeSettings, announcementSettings, todayKey]);

	if (loading) return <div>Loading finance details...</div>;
	if (error) return <div style={{ color: 'red' }}>{error}</div>;

	// Calculate fees
	let total = 0;
	const feeRows = fees.map((fee, idx) => {
		let amount = 0;
		let formula = "";
		if (
			fee.formula &&
			fee.formula.toLowerCase().includes("units") &&
			typeof fee.amount === "number"
		) {
			// Dynamic per-unit fee based on formula/note
			amount = units * fee.amount;
			formula = `(${units} units X ${fee.amount})`;
		} else if (fee.perUnit !== undefined) {
			amount = units * fee.perUnit;
			formula = `(${units} units X ${fee.perUnit})`;
		} else if (fee.formula) {
			formula = fee.formula;
			amount = fee.amount || 0;
		} else {
			amount = fee.amount || 0;
		}
		total += amount;
		return (
			<tr key={idx}>
				<td>
					{fee.label}
					{formula && (
						<span style={{ color: '#888', fontSize: 13, marginLeft: 6 }}>
							{formula}
						</span>
					)}
				</td>
				<td style={{ textAlign: 'right', borderBottom: '2px solid #bdbdbd', color: '#4b3c0a' }}>
					{amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
				</td>
			</tr>
		);
	});

	const totalWithDiscount = total - cashDiscount;
	const installment = parseFloat((total / 5).toFixed(2));

	// Helper for date formatting
	function formatDate(dateStr) {
		if (!dateStr) return "N/A";
		const d = new Date(dateStr);
		if (isNaN(d.getTime())) return "N/A";
		return d.toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric"
		});
	}

	return (
		<div style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "row" }}>
			<div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
				<div style={{ flex: 1, padding: 40, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
					<div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)', border: '1px solid #e5e7eb', padding: 40, width: '90%', margin: '0 auto', marginTop: 20 }}>
						<div style={{ display: 'flex', justifyContent: 'space-between', gap: 40 }}>
							{/* Left: Miscellaneous Fees Table */}
							<div style={{ flex: 1 }}>
								<div style={{ fontWeight: 700, fontSize: 20, marginBottom: 18, letterSpacing: 1 }}>MISCELLANEOUS FEES</div>
								<table style={{ width: '100%', fontSize: 16, marginBottom: 18 }}>
									<thead>
										<tr style={{ borderBottom: '2px solid #222' }}>
											<th style={{ textAlign: 'left', fontWeight: 500 }}> </th>
											<th style={{ textAlign: 'right', fontWeight: 500 }}>Amount</th>
										</tr>
									</thead>
									<tbody>
										{feeRows}
										<tr>
											<td style={{ fontWeight: 700, fontSize: 17, paddingTop: 10 }}>Total:</td>
											<td style={{ textAlign: 'right', fontWeight: 700, fontSize: 17, paddingTop: 10 }}>
												{total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
											</td>
										</tr>
									</tbody>
								</table>
								<div style={{ fontWeight: 700, color: '#18803b', fontSize: 17, margin: '30px 0 8px 0' }}>
									OPTION 1: CASH PAYMENT WITH DISCOUNT
									<span style={{ float: 'right' }}>{totalWithDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
								</div>
								<div style={{ color: '#222', fontSize: 15, marginBottom: 18 }}>
									If full payment is made on or before {cashWithDiscountDate ? formatDate(cashWithDiscountDate) : "N/A"}.
								</div>
								<div style={{ fontWeight: 700, color: '#18803b', fontSize: 17, margin: '30px 0 8px 0' }}>
									OPTION 2: CASH PAYMENT WITHOUT DISCOUNT
									<span style={{ float: 'right' }}>{total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
								</div>
								<div style={{ color: '#222', fontSize: 15, marginBottom: 18 }}>
									If full payment is made after {cashWithDiscountDate ? formatDate(cashWithDiscountDate) : "N/A"}.
								</div>
							</div>
							{/* Right: Installment Option */}
							<div style={{ flex: 1, minWidth: 320 }}>
								<div style={{ fontWeight: 700, color: '#18803b', fontSize: 18, marginBottom: 18 }}>OPTION 3: INSTALLMENT PAYMENT</div>
								<table style={{ width: '100%', fontSize: 16, marginBottom: 18 }}>
									<tbody>
										{installmentDates.map((date, idx) => (
											<tr key={idx}>
												<td>{["Downpayment", "1st Installment", "2nd Installment", "3rd Installment", "4th Installment"][idx]}</td>
												<td>{date ? formatDate(date) : "N/A"}</td>
												<td style={{ textAlign: 'right' }}>{installment.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
											</tr>
										))}
										<tr>
											<td style={{ fontWeight: 700, fontSize: 17, paddingTop: 10 }}>Total:</td>
											<td></td>
											<td style={{ textAlign: 'right', fontWeight: 700, fontSize: 17, paddingTop: 10 }}>
												{total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
											</td>
										</tr>
									</tbody>
								</table>
								<div style={{ background: '#fff8f1', border: '1px solid #ff9800', borderRadius: 6, padding: 18, color: '#e67e22', fontWeight: 600, fontSize: 16, marginTop: 90 }}>
									<div style={{ color: '#e67e22', fontWeight: 700, marginBottom: 8 }}>NOTE</div>
									<div style={{ color: '#444', fontWeight: 400, fontSize: 15 }}>
										Please check your Miscellaneous Fees breakdown carefully before proceeding.<br />
										Once verified, you may proceed to the campus Finances Office to settle your payment.
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
